import type { FeatureFlags, FeatureKey, MenuConfig } from "@repo/types"
import { BarChart3, Smartphone, Bell, BookOpen, Briefcase, ClipboardCheck, ClipboardList, Layers, LayoutTemplate, List, Map, Palette, Phone, Plus, Rows2, Settings, Shield, Sparkles, Sprout, TreePine, UserCog, Users, WandSparkles, Zap } from "lucide-react"
import type * as React from "react"

// Type definition for menu items
export interface MenuItem {
  name: string;
  href: string;
  icon: React.ElementType;
  subMenu?: MenuItem[];
  roles?: string[]; // Array of roles that can access this item
  /** Fonctionnalité activable par client (espace /superadmin) : menu masqué si elle est désactivée. */
  feature?: FeatureKey;
}

// Function to create navigation items with labels
export const createNavigationItems = (sidebarLabels: any): MenuItem[] => [
  { name: sidebarLabels.navigation.dashboard, href: "/admin/dashboard", icon: BarChart3, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
  { name: sidebarLabels.navigation.mapView, href: "/admin/inventaires/map", icon: Map, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
  {
    name: sidebarLabels.navigation.treeInventory,
    href: "/admin/inventaires",
    icon: TreePine,
    roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.navigation.inventaireArbre, href: "/admin/inventaires?type=ARBRE", icon: TreePine, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.navigation.inventaireAlignement, href: "/admin/inventaires?type=ALIGNEMENT", icon: Rows2, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.navigation.inventaireEspaceBoise, href: "/admin/inventaires?type=EB", icon: Sprout, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ]
  },
  { name: sidebarLabels.navigation.validationPending, href: "/admin/inventaires/not-validated", icon: ClipboardList, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
  {
    name: sidebarLabels.navigation.workManagement,
    href: "/travaux",
    icon: Briefcase,
    roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.workManagement.alerts, href: "/admin/inventaires/alerts", icon: Bell, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.workManagement.work, href: "/admin/inventaires/travaux", icon: ClipboardCheck, roles: ["MANAGER", "ADMIN", "DENDROMAP", "AGENT"] },
      { name: sidebarLabels.workManagement.visite, href: "/admin/inventaires/visites", icon: ClipboardList, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ]
  },

  {
    name: sidebarLabels.navigation.newPlantation,
    href: "/admin/plantations",
    icon: Plus,
    roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.newPlantation.createArbre, href: "/admin/plantations?create=arbre", icon: TreePine, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.newPlantation.createAlignement, href: "/admin/plantations?create=alignement", icon: Rows2, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.newPlantation.createEspaceBoise, href: "/admin/plantations?create=epaysage", icon: Sprout, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ],
  },

  {
    name: sidebarLabels.navigation.library,
    href: "/admin/bibliotheque",
    icon: BookOpen,
    roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.library.essences, href: "/admin/bibliotheque/essences", icon: TreePine, roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
      { name: sidebarLabels.library.mushrooms, href: "/admin/bibliotheque/champignons", icon: Sprout, roles: ["USER", "ADMIN", "DENDROMAP", "AGENT", "MANAGER"] },
    ],
  },
  { name: sidebarLabels.navigation.smartMap, href: "/admin/smart-map", icon: WandSparkles, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"], feature: "smart_map" },
  { name: sidebarLabels.navigation.aiAssistant, href: "/admin/ai/assistant", icon: Sparkles, roles: ["ADMIN", "DENDROMAP", "AGENT", "MANAGER"], feature: "ai_assistant" },
]

export const createConfigurationItems = (sidebarLabels: any): MenuItem[] => [
  { name: sidebarLabels.configuration.contactManagement, href: "/admin/contacts", icon: Phone, roles: ["ADMIN"] },
  { name: sidebarLabels.configuration.geoLayers, href: "/admin/geo-layers", icon: Layers, roles: ["ADMIN"] },
  { name: sidebarLabels.configuration.printTemplates, href: "/admin/print-templates", icon: LayoutTemplate, roles: ["ADMIN", "DENDROMAP", "MANAGER"], feature: "print_templates" },
]

export const createAdministrationItems = (sidebarLabels: any): MenuItem[] => [
  {
    name: sidebarLabels.administration.rightsManagement,
    href: "/admin/droits",
    icon: Shield,
    roles: ["ADMIN", "DENDROMAP", "MANAGER"],
    subMenu: [
      { name: sidebarLabels.rightsManagement.group, href: "/admin/droits/groupe", icon: Users, roles: ["ADMIN"] },
      { name: sidebarLabels.rightsManagement.profile, href: "/admin/droits/profil", icon: UserCog, roles: ["ADMIN"] },
      { name: sidebarLabels.rightsManagement.user, href: "/admin/droits/utilisateur", icon: Users, roles: ["ADMIN", "DENDROMAP", "MANAGER"] },
      { name: sidebarLabels.rightsManagement.quickCreate, href: "/admin/droits/creation-rapide", icon: Zap, roles: ["ADMIN"] },
    ]
  },
  {
    name: sidebarLabels.administration.savedLists,
    href: "/listes",
    icon: List,
    roles: ["ADMIN"],
    subMenu: [
      { name: sidebarLabels.savedLists.essences, href: "/admin/config-inventaire/essences", icon: TreePine, roles: ["ADMIN"] },
      { name: sidebarLabels.savedLists.mushrooms, href: "/admin/config-inventaire/champignons", icon: Sprout, roles: ["ADMIN"] },
    ]
  },
]


/** Pages de /admin toujours accessibles : on ne peut pas les masquer (sinon plus de page d'atterrissage). */
export const LOCKED_MENU_HREFS: ReadonlySet<string> = new Set(["/admin/dashboard"])

/** Menus de l'espace /superadmin (réglages plateforme). */
export const createSuperAdminItems = (sidebarLabels: any): MenuItem[] => {
  const labels = sidebarLabels.superadmin
  return [
    { name: labels.metrics, href: "/superadmin/metrics", icon: BarChart3 },
    { name: labels.clients, href: "/superadmin/clients", icon: Users },
    { name: labels.features, href: "/superadmin/features", icon: Sparkles },
    { name: labels.menus, href: "/superadmin/menus", icon: List },
    { name: labels.announcements, href: "/superadmin/announcements", icon: Bell },
    { name: labels.designSystem, href: "/superadmin/design-system", icon: Palette },
    { name: labels.mobileReleases, href: "/superadmin/mobile-releases", icon: Smartphone },
    { name: labels.pricing, href: "/superadmin/subscription-settings", icon: Settings },
    { name: labels.promoCode, href: "/superadmin/promo-codes", icon: Settings },
    { name: labels.aiUsage, href: "/superadmin/ai-usage", icon: BarChart3 },
    { name: labels.aiSettings, href: "/superadmin/ai/settings", icon: Sparkles },
    { name: labels.auditLogs, href: "/superadmin/audit-logs", icon: Shield },
    {
      name: labels.debug,
      href: "/superadmin/debug",
      icon: Zap,
      subMenu: [
        { name: labels.paymentStats, href: "/superadmin/debug/payment-stats", icon: BarChart3 },
        { name: labels.paymentLifecycle, href: "/superadmin/debug/payment-lifecycle", icon: ClipboardList },
        { name: labels.sendTestSms, href: "/superadmin/debug/send-test-sms", icon: Phone },
        { name: labels.subscriptionHelper, href: "/superadmin/debug/subscription-helper", icon: ClipboardCheck },
      ],
    },
  ]
}

/** Rôles qui voient le menu : ceux réglés par le superadmin, sinon ceux par défaut. */
export function effectiveMenuRoles(item: MenuItem, config: MenuConfig | undefined): string[] | undefined {
  if (LOCKED_MENU_HREFS.has(item.href)) return item.roles
  return config?.[item.href] ?? item.roles
}

function isVisibleForRole(item: MenuItem, role: string, config: MenuConfig | undefined, features?: Partial<FeatureFlags>): boolean {
  if (item.feature && features?.[item.feature] === false) return false
  const roles = effectiveMenuRoles(item, config)
  return !roles || roles.length === 0 ? !config?.[item.href] : roles.includes(role)
}

/** Filtre les menus et sous-menus selon le rôle et la configuration (un parent masqué masque ses enfants). */
export function filterMenuItemsForRole(
  items: MenuItem[],
  role: string | null,
  config?: MenuConfig,
  features?: Partial<FeatureFlags>,
): MenuItem[] {
  if (!role) return items
  return items
    .filter((item) => isVisibleForRole(item, role, config, features))
    .map((item) =>
      item.subMenu ? { ...item, subMenu: item.subMenu.filter((sub) => isVisibleForRole(sub, role, config, features)) } : item,
    )
}

const pathOf = (href: string): string => href.split("?")[0] ?? href
const matches = (pathname: string, href: string) => {
  const path = pathOf(href)
  return pathname === path || pathname.startsWith(path + "/")
}

/**
 * Vrai si la page est masquée pour ce rôle : le menu le plus précis qui la couvre (ou son parent) n'est pas
 * visible. Une page qu'aucun menu ne couvre reste accessible.
 */
export function isPathHiddenForRole(
  pathname: string,
  items: MenuItem[],
  role: string | null,
  config: MenuConfig | undefined,
  features?: Partial<FeatureFlags>,
): boolean {
  const hasDisabledFeature = Object.values(features ?? {}).some((enabled) => enabled === false)
  if (!role || (!hasDisabledFeature && (!config || Object.keys(config).length === 0))) return false
  let best: { item: MenuItem; parent?: MenuItem; length: number } | undefined
  const candidates: Array<{ item: MenuItem; parent?: MenuItem }> = items.flatMap((item) => [
    { item },
    ...(item.subMenu ?? []).map((sub) => ({ item: sub, parent: item })),
  ])
  for (const candidate of candidates) {
    if (!matches(pathname, candidate.item.href)) continue
    const length = pathOf(candidate.item.href).length
    if (!best || length > best.length) best = { ...candidate, length }
  }
  if (!best) return false
  if (best.parent && !isVisibleForRole(best.parent, role, config, features)) return true
  return !isVisibleForRole(best.item, role, config, features)
}
